import * as React from "react";

import { Container, Stack, AspectRatio } from "@mantine/core";
import objectHash from "object-hash";

import { useDocumentStyles } from "../../../hooks/useDocumentStyles";
import { useCurrentEditorDocument } from "../../../hooks/useEditorState";
import {
  ChecklistDocumentElement,
  ChecklistElement,
  ChecklistHeader,
  ChecklistItem,
  isChecklistHeader,
  isChecklistItem,
  isPageBreak,
} from "../../../lib/model/checklistAppState";
import { ChecklistDocumentStyles } from "../../../lib/model/documentStyles";

import { Page, Text, View } from "./SoftPDFComponents";

const partitionPageIndexes = (
  checklistDocumentElements: ChecklistDocumentElement[]
): number[][] => {
  const partitions: number[][] = [[]];

  checklistDocumentElements.forEach((checklistDocumentElement, idx) => {
    if (isPageBreak(checklistDocumentElement)) {
      partitions.push([]);
    } else {
      partitions.at(-1)?.push(idx);
    }
  });

  return partitions;
};

export const LivePDFPreview = (): JSX.Element => {
  const documentStyles = useDocumentStyles();
  const documentPages = JSON.parse(
    useCurrentEditorDocument(
      (state) => JSON.stringify(partitionPageIndexes(state.contents.checklists)),
      "[]"
    )
  ) as number[][];
  const documentLayout = useCurrentEditorDocument((state) => state.layout, "twoColumn");
  const documentTitle = useCurrentEditorDocument((state) => state.contents.title, {
    id: "",
    content: "",
  });

  const enumeration = (checklistPage: number[]): JSX.Element => {
    switch (documentLayout) {
      case "twoColumn":
        return (
          <AspectRatio
            ratio={8.5 / 10}
            style={{
              justifyContent: "flex-start !important",
              flex: "0 0 8.5in",
              minWidth: "8.5in",
            }}
          >
            <View
              style={{
                justifyContent: "flex-start !important",
                justifyItems: "start",
                ...documentStyles.checklistDocumentBody,
              }}
            >
              {checklistPage.map((checklistIdx) => {
                return (
                  <ChecklistPreview
                    key={checklistIdx}
                    index={checklistIdx}
                    stylesheetSnapshot={documentStyles}
                  />
                );
              })}
            </View>
          </AspectRatio>
        );
      case "oneColumn":
        return (
          <View
            style={{
              justifyContent: "flex-start !important",
              flex: "0 0 5.5in",
            }}
          >
            <View
              style={{
                justifyContent: "flex-start !important",
                justifyItems: "start",
                ...documentStyles.checklistDocumentBody,
              }}
            >
              {checklistPage.map((checklistIdx) => {
                return (
                  <ChecklistPreview
                    key={checklistIdx}
                    index={checklistIdx}
                    stylesheetSnapshot={documentStyles}
                  />
                );
              })}
            </View>
          </View>
        );
    }
    const _exhaustiveCheck: never = documentLayout;
    return _exhaustiveCheck;
  };

  return (
    <Container style={{ width: "100%" }}>
      <Stack style={{ width: documentLayout === "oneColumn" ? "8.5in" : undefined }}>
        {documentPages.map((checklistPage, idx) => (
          <Page
            style={{
              ...documentStyles.checklistDocument,
            }}
            key={`page-${idx}`}
          >
            {!!documentTitle.content && (
              <Text style={documentStyles.checklistDocumentTitle}>{documentTitle.content}</Text>
            )}
            <Stack justify="flex-start">{enumeration(checklistPage)}</Stack>
          </Page>
        ))}
      </Stack>
    </Container>
  );
};

const ChecklistPreview = ({
  index,
  stylesheetSnapshot,
}: {
  index: number;
  stylesheetSnapshot: ChecklistDocumentStyles;
}): JSX.Element | null => {
  const checklist = useCurrentEditorDocument((state) => state.contents.checklists[index], null);
  const documentLayout = useCurrentEditorDocument((state) => state.layout, "twoColumn");
  if (!checklist || isPageBreak(checklist)) {
    return null;
  }
  return (
    <View
      style={{
        ...stylesheetSnapshot.checklist,
        width: `${documentLayout === "twoColumn" ? "49%" : "100%"}`,
      }}
    >
      <Text style={stylesheetSnapshot.checklistTitle}>{checklist.title.content}</Text>
      <View style={stylesheetSnapshot.checklistBody}>
        {checklist.content.map((checklistElement: ChecklistElement) => {
          if (isChecklistItem(checklistElement)) {
            return (
              <ChecklistItemPreview
                key={objectHash(checklistElement)}
                checklistItem={checklistElement}
                stylesheetSnapshot={stylesheetSnapshot}
              />
            );
          } else if (isChecklistHeader(checklistElement)) {
            return (
              <ChecklistHeaderPreview
                key={objectHash(checklistElement)}
                checklistHeader={checklistElement}
                stylesheetSnapshot={stylesheetSnapshot}
              />
            );
          }
        })}
      </View>
    </View>
  );
};

const ChecklistItemPreview = ({
  checklistItem,
  stylesheetSnapshot,
}: {
  checklistItem: ChecklistItem;
  stylesheetSnapshot: ChecklistDocumentStyles;
}): JSX.Element => {
  const autoCapitalize = useCurrentEditorDocument((state) => state.options.autoCapitalize, "none");

  const itemContent =
    autoCapitalize === "both" || autoCapitalize === "checklist-item"
      ? checklistItem.item.content.toUpperCase()
      : checklistItem.item.content;
  const itemSetting =
    autoCapitalize === "both" || autoCapitalize === "checklist-setting"
      ? checklistItem.setting.content.toUpperCase()
      : checklistItem.setting.content;

  const styles = { ...stylesheetSnapshot.checklistItem, ...checklistItem.styles };

  return (
    <View style={styles}>
      <Text style={stylesheetSnapshot.checklistItemLabel}>{itemContent}</Text>
      <Text style={{ ...stylesheetSnapshot.checklistItemSeparatorText, height: "1em" }}>
        {".".repeat(1000)}
      </Text>
      <Text style={stylesheetSnapshot.checklistItemSetting}>{itemSetting}</Text>
    </View>
  );
};

const ChecklistHeaderPreview = ({
  checklistHeader,
  stylesheetSnapshot,
}: {
  checklistHeader: ChecklistHeader;
  stylesheetSnapshot: ChecklistDocumentStyles;
}): JSX.Element => {
  const styles = { ...stylesheetSnapshot.checklistHeader, ...checklistHeader.styles };
  return (
    <View style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Text style={styles}>{checklistHeader.text.content}</Text>
    </View>
  );
};
